import { Button } from "@bowfast/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bowfast/ui/components/dialog";
import { Input } from "@bowfast/ui/components/input";
import { Label } from "@bowfast/ui/components/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@bowfast/ui/components/select";
import { PlusIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { LEVEL_TIER_LABEL, RATING_SYSTEM_LABEL } from "~/pages/skills/skills.config";
import type { RatingSystem, Skill, SkillLevelTier } from "~/pages/skills/skills.types";

export interface SkillFormData {
  name: string;
  description: string | null;
  ratingSystem: RatingSystem;
  level: SkillLevelTier | null;
}

interface SkillDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  skill?: Skill;
  categoryName: string;
  onSave: (data: SkillFormData) => void;
  isSaving?: boolean;
}

export function SkillDialog({ open, onOpenChange, skill, categoryName, onSave, isSaving }: Readonly<SkillDialogProps>) {
  const isEdit = !!skill;
  const [name, setName] = useState(skill?.name ?? "");
  const [description, setDescription] = useState(skill?.description ?? "");
  const [ratingSystem, setRatingSystem] = useState<RatingSystem>(skill?.ratingSystem ?? "PASS_FAIL");
  const [level, setLevel] = useState<SkillLevelTier | "">(skill?.level ?? "");

  useEffect(() => {
    if (open) {
      setName(skill?.name ?? "");
      setDescription(skill?.description ?? "");
      setRatingSystem(skill?.ratingSystem ?? "PASS_FAIL");
      setLevel(skill?.level ?? "");
    }
  }, [open, skill]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      description: description.trim() || null,
      ratingSystem,
      level: level || null,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit skill" : "New skill"}</DialogTitle>
          <DialogDescription>
            {isEdit ? `Editing "${skill?.name}" in ${categoryName}.` : `Add a skill to the ${categoryName} category.`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="skill-name">Skill name</Label>
            <Input
              id="skill-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Man overboard drill"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="skill-desc">
              Description <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="skill-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of what is assessed"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Rating system</Label>
              <Select value={ratingSystem} onValueChange={(v) => setRatingSystem(v as RatingSystem)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {(Object.keys(RATING_SYSTEM_LABEL) as RatingSystem[]).map((k) => (
                      <SelectItem key={k} value={k}>
                        {RATING_SYSTEM_LABEL[k]}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>
                Level tier <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Select value={level} onValueChange={(v) => setLevel(v as SkillLevelTier | "")}>
                <SelectTrigger>
                  <SelectValue placeholder="Any level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="">Any level</SelectItem>
                    {(Object.keys(LEVEL_TIER_LABEL) as SkillLevelTier[]).map((k) => (
                      <SelectItem key={k} value={k}>
                        {LEVEL_TIER_LABEL[k]}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          {ratingSystem !== "PASS_FAIL" && (
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <p className="mb-2 text-xs font-semibold text-muted-foreground">Rating options</p>
              {(skill?.levels.length
                ? skill.levels
                : ratingSystem === "LETTER_GRADE"
                  ? [
                      { id: "x1", label: "A", numericValue: 4, sortOrder: 0 },
                      { id: "x2", label: "B", numericValue: 3, sortOrder: 1 },
                      { id: "x3", label: "C", numericValue: 2, sortOrder: 2 },
                    ]
                  : [
                      { id: "x4", label: "Excellent", numericValue: 5, sortOrder: 0 },
                      { id: "x5", label: "Good", numericValue: 4, sortOrder: 1 },
                    ]
              ).map((l) => (
                <div key={l.id} className="mb-1.5 flex items-center gap-2">
                  <Input className="h-7 flex-1 text-xs" defaultValue={l.label} />
                  {(ratingSystem === "NUMERIC" || ratingSystem === "LETTER_GRADE") && (
                    <Input
                      className="h-7 w-16 text-xs"
                      type="number"
                      defaultValue={l.numericValue ?? ""}
                      placeholder="0"
                    />
                  )}
                </div>
              ))}
              <Button size="sm" variant="ghost" className="mt-1 gap-1.5 text-xs text-muted-foreground" type="button">
                <PlusIcon className="size-3" />
                Add option
              </Button>
            </div>
          )}

          <DialogFooter showCloseButton>
            <Button type="submit" {...(isSaving || !name.trim() ? { disabled: true } : {})}>
              {isEdit ? "Save changes" : "Add skill"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
